/**
 * Auto-Select Class for Gas Spring Configurator (Client-Side)
 * 
 * This class handles the client-side logic for auto-selecting the best gas spring combinations.
 * It communicates with the server to get ranked combinations and processes the response,
 * including extension data when present.
 * 
 * EXTENSION HANDLING:
 * - Receives extension data from server in the combination response
 * - Sets cf.ex globally when an extension is selected
 * - Handles both extension and non-extension combinations
 * - Updates length warnings to include extension information
 */
class auto_select {

    constructor() {
        this.length_combi_index = 0;
        this.sorted_length_combis = [];
        this.auto_select = false;
        this.gs_desc_list = []; // List of gas spring descriptions from combinations
        this.is_fresh_calculation = false; // Flag to track if this is a fresh calculation (should set gas spring)
    }

    reset() {
        this.length_combi_index = 0;
        this.sorted_length_combis = [];
        this.auto_select = false;
        this.gs_desc_list = [];
        this.is_fresh_calculation = false;
    }

    /**
     * Build top unique gas spring combinations for shared links.
     *
     * @param {number} limit Maximum number of combinations to return.
     * @returns {Array}
     */
    build_gs_ranked_list(limit, skip_extension_filter) {
        var max_items = limit || 5;
        var use_extensions = $('#use_extension_checkbox').is(':checked');
        var ranked = [];
        var seen = {};
        var i;
        var combo;
        var combo_ex;

        for (i = 0; i < this.sorted_length_combis.length; i++) {
            combo = this.sorted_length_combis[i];
            combo_ex = combo.ex || 'none';

            if (!skip_extension_filter && !use_extensions && combo_ex !== 'none') {
                continue;
            }

            if (!seen[combo.gs]) {
                seen[combo.gs] = true;
                ranked.push({
                    gs: combo.gs,
                    mp_cyl: combo.mp_cyl,
                    mp_rod: combo.mp_rod,
                    ex: combo_ex,
                    has_extender: combo_ex !== 'none'
                });
                if (ranked.length >= max_items) {
                    break;
                }
            }
        }

        return ranked;
    }

    /**
     * Normalize ranked combination objects from cookie/share payloads.
     *
     * @param {Array} ranked Ranked combination list.
     * @returns {Array}
     */
    normalize_ranked_combos(ranked) {
        if (!Array.isArray(ranked)) {
            return [];
        }

        return ranked.map(function (combo) {
            var combo_ex = combo.ex || 'none';

            return {
                gs: combo.gs,
                mp_cyl: combo.mp_cyl,
                mp_rod: combo.mp_rod,
                ex: combo_ex,
                has_extender: combo_ex !== 'none'
            };
        });
    }

    /**
     * Build ranked gas spring combinations for cookie persistence.
     *
     * @param {number} limit Maximum number of combinations to return.
     * @returns {Array}
     */
    build_gs_ranked_for_cookie(limit) {
        var extender_type = $('#extender_type').val() || '';

        if ((!extender_type || extender_type === 'none') && typeof cf !== 'undefined' && cf.ex && cf.ex.filename) {
            extender_type = cf.ex.filename;
        }

        var skip_extension_filter = !!extender_type && extender_type !== 'none';
        var ranked = this.build_gs_ranked_list(limit, skip_extension_filter);

        return this.ensure_active_combination_in_ranked(ranked, extender_type, limit);
    }

    /**
     * Ensure the active gas spring + mounting parts (+ extender) are stored in ranked combos.
     *
     * build_gs_ranked_list only keeps one combo per gas spring (the best ranked one).
     * When the user picks different mounting parts, those must still be persisted for
     * reload and short-link restore.
     *
     * @param {Array} ranked Ranked combination list.
     * @param {string} extender_type Active extender filename.
     * @param {number} limit Maximum number of combinations to return.
     * @returns {Array}
     */
    ensure_active_combination_in_ranked(ranked, extender_type, limit) {
        var max_items = limit || 5;
        var formData = typeof fd !== 'undefined' ? fd.get() : null;
        var i;
        var combo;
        var matchIndex = -1;
        var active_ex = (!extender_type || extender_type === 'none') ? 'none' : extender_type;
        var activeCombo = null;
        var active_gs = '';
        var active_mp_cyl = '';
        var active_mp_rod = '';

        if (!Array.isArray(ranked)) {
            ranked = [];
        }

        // Prefer live configurator objects (same source of truth as extender sync).
        if (typeof cf !== 'undefined') {
            if (cf.gs && cf.gs.description) {
                active_gs = cf.gs.description;
            }
            if (cf.mp_cyl && cf.mp_cyl.hahntype) {
                active_mp_cyl = cf.mp_cyl.hahntype;
            }
            if (cf.mp_rod && cf.mp_rod.hahntype) {
                active_mp_rod = cf.mp_rod.hahntype;
            }
        }

        if (formData) {
            if (!active_gs) {
                active_gs = formData.gasspring || '';
            }
            if (!active_mp_cyl) {
                active_mp_cyl = formData.mp_cyl || '';
            }
            if (!active_mp_rod) {
                active_mp_rod = formData.mp_rod || '';
            }
        }

        if (!active_gs || !active_mp_cyl || !active_mp_rod) {
            return ranked.slice(0, max_items);
        }

        ranked = this.normalize_ranked_combos(ranked);

        for (i = 0; i < ranked.length; i++) {
            combo = ranked[i];

            if (combo.gs === active_gs &&
                combo.mp_cyl === active_mp_cyl &&
                combo.mp_rod === active_mp_rod &&
                (combo.ex || 'none') === active_ex) {
                matchIndex = i;
                activeCombo = {
                    gs: combo.gs,
                    mp_cyl: combo.mp_cyl,
                    mp_rod: combo.mp_rod,
                    ex: active_ex,
                    has_extender: active_ex !== 'none'
                };
                break;
            }
        }

        if (matchIndex === -1) {
            activeCombo = {
                gs: active_gs,
                mp_cyl: active_mp_cyl,
                mp_rod: active_mp_rod,
                ex: active_ex,
                has_extender: active_ex !== 'none'
            };
            ranked = ranked.filter(function (item) {
                return item.gs !== active_gs;
            });
            ranked.unshift(activeCombo);
        } else {
            ranked.splice(matchIndex, 1);
            ranked = ranked.filter(function (item) {
                return item.gs !== active_gs;
            });
            ranked.unshift(activeCombo);
        }

        return ranked.slice(0, max_items);
    }

    /**
     * Alias for ensure_active_combination_in_ranked (kept for compatibility).
     *
     * @param {Array} ranked Ranked combination list.
     * @param {string} extender_type Active extender filename.
     * @param {number} limit Maximum number of combinations to return.
     * @returns {Array}
     */
    ensure_active_extender_in_ranked(ranked, extender_type, limit) {
        return this.ensure_active_combination_in_ranked(ranked, extender_type, limit);
    }

    /**
     * Read the use_extensions flag from parsed cookie data.
     *
     * @param {Array|null} cookie_data Parsed configurator cookie data.
     * @returns {string}
     */
    get_use_extensions_from_cookie(cookie_data) {
        var i;

        if (!cookie_data || !Array.isArray(cookie_data)) {
            return '';
        }

        for (i = 0; i < cookie_data.length; i++) {
            if (cookie_data[i].name === 'use_extensions') {
                return cookie_data[i].value || '';
            }
        }

        return '';
    }

    /**
     * Find the restored combination index for the stored cookie selection.
     *
     * @param {Array|null} cookie_data Parsed configurator cookie data.
     * @returns {number}
     */
    find_stored_combination_index(cookie_data) {
        var stored_gasspring = null;
        var stored_mp_cyl = null;
        var stored_mp_rod = null;
        var stored_extender_type = 'none';
        var use_extensions = '';
        var i;
        var combo;

        if (!cookie_data || !Array.isArray(cookie_data)) {
            return 0;
        }

        for (i = 0; i < cookie_data.length; i++) {
            if (cookie_data[i].name === 'gasspring') {
                stored_gasspring = cookie_data[i].value;
            } else if (cookie_data[i].name === 'mp_cyl') {
                stored_mp_cyl = cookie_data[i].value;
            } else if (cookie_data[i].name === 'mp_rod') {
                stored_mp_rod = cookie_data[i].value;
            } else if (cookie_data[i].name === 'extender_type') {
                stored_extender_type = cookie_data[i].value || 'none';
            } else if (cookie_data[i].name === 'use_extensions') {
                use_extensions = cookie_data[i].value || '';
            }
        }

        if (typeof cf !== 'undefined' && cf.resolve_extender_type_from_cookie) {
            var resolved_extender = cf.resolve_extender_type_from_cookie(cookie_data);

            if (resolved_extender) {
                stored_extender_type = resolved_extender;
            }
        }

        for (i = 0; i < this.sorted_length_combis.length; i++) {
            combo = this.sorted_length_combis[i];

            if (combo.gs === stored_gasspring &&
                combo.mp_cyl === stored_mp_cyl &&
                combo.mp_rod === stored_mp_rod &&
                (combo.ex || 'none') === (stored_extender_type || 'none')) {
                return i;
            }
        }

        if ((stored_extender_type && stored_extender_type !== 'none') || use_extensions === 'on') {
            for (i = 0; i < this.sorted_length_combis.length; i++) {
                combo = this.sorted_length_combis[i];

                if (combo.gs === stored_gasspring &&
                    combo.mp_cyl === stored_mp_cyl &&
                    combo.mp_rod === stored_mp_rod &&
                    combo.ex && combo.ex !== 'none') {
                    return i;
                }
            }
        }

        for (i = 0; i < this.sorted_length_combis.length; i++) {
            combo = this.sorted_length_combis[i];

            if (combo.gs === stored_gasspring &&
                combo.mp_cyl === stored_mp_cyl &&
                combo.mp_rod === stored_mp_rod) {
                return i;
            }
        }

        return 0;
    }

    /**
     * Unique gas spring descriptions from the ranked combination list.
     *
     * @param {number} limit Maximum number of options to return.
     * @returns {string[]}
     */
    get_unique_gasspring_descriptions(limit) {
        var skip_extension_filter = typeof cf !== 'undefined' && cf.loading_from_shared_link;
        var ranked = this.build_gs_ranked_list(limit || 5, skip_extension_filter);
        var descriptions = ranked.map(function (combo) {
            return combo.gs;
        });
        var formData = typeof fd !== 'undefined' ? fd.get() : null;

        if (formData && formData.gasspring && descriptions.indexOf(formData.gasspring) === -1) {
            descriptions.unshift(formData.gasspring);
        }

        return descriptions.slice(0, limit || 5);
    }

    /**
     * Find the first combination index for a gas spring description.
     *
     * @param {string} gasspring_desc Gas spring description.
     * @returns {number}
     */
    find_combination_index_by_gs(gasspring_desc, cookie_data) {
        var i;
        var stored_index;

        if (cookie_data) {
            stored_index = this.find_stored_combination_index(cookie_data);

            if (stored_index >= 0 &&
                stored_index < this.sorted_length_combis.length &&
                this.sorted_length_combis[stored_index].gs === gasspring_desc) {
                return stored_index;
            }
        }

        for (i = 0; i < this.sorted_length_combis.length; i++) {
            if (this.sorted_length_combis[i].gs === gasspring_desc) {
                return i;
            }
        }

        return -1;
    }

    /**
     * Send AJAX request to server to calculate best combinations
     * 
     * This method sends the user's requirements to the server and receives back
     * a ranked list of combinations, some of which may include extensions.
     * 
     * SERVER RESPONSE STRUCTURE:
     * Each combination in the response contains:
     * - gs: Gas spring description
     * - mp_cyl: Cylinder mounting part type
     * - mp_rod: Rod mounting part type  
     * - ex: Extension filename (or "none" if no extension)
     * - has_extender: Boolean flag indicating extension presence
     * 
     * @returns {Promise} AJAX promise
     */
    async calculate_length_combis_ajax () {
        
        var datatosend = fd.get();
        datatosend.action = 'fl_gasspring_configurator_auto_select';
        datatosend.sub_action = 'calculate_length_combis';
        datatosend.security = texts.security;

        // Add extension preference to the data sent to server
        datatosend.use_extensions = $('#use_extension_checkbox').is(':checked') ? 'on' : 'off';

        var self = this;
        return $.ajax({
            url: texts.ajaxurl,
            data: datatosend,
            type: 'post',
            success: function (result) {
                var result_decoded = JSON.parse(result);
                // console.log(result_decoded);
                self.sorted_length_combis = result_decoded;
                self.gs_desc_list = self.sorted_length_combis.map(item => item.gs);
                
                // Set the extender type from the first combination (if available and extensions are enabled)
                if (result_decoded && result_decoded.length > 0) {
                    var first_combo = result_decoded[0];
                    var use_extensions = $('#use_extension_checkbox').is(':checked');

                    // Handle extension data from the selected combination
                    if (use_extensions) {
                        var extender_type = first_combo.ex || 'none';
                        $('#extender_type').val(extender_type);
                    } else {
                        // Clear extender_type when extensions are disabled
                        $('#extender_type').val('');
                    }
                    
                    // Auto-check only when extensions are still enabled. Never turn the
                    // toggle back on after the user has switched it off in step 2.
                    if (use_extensions && first_combo.has_extender) {
                        $('#use_extension_checkbox').prop('checked', true);
                        cf.cookie.set();
                    }
                }
            },
            error: function (xhr, status, err) {
            }
        });
    }
    
    /**
     * Calculate length combinations by calling the server
     * 
     * This resets the state and triggers the server calculation.
     * The server will return combinations ranked by length accuracy,
     * with extensions deprioritized (only used when they provide measurable improvement).
     */
    async calculate_length_combis() {
        this.reset();
        this.is_fresh_calculation = true;
        await this.calculate_length_combis_ajax();

        var cookie_data = cf.cookie ? cf.cookie.get() : null;
        this.add_stored_combination_as_first(cookie_data);
    }

    /**
     * Load ranked gas spring alternatives for the shared-link dropdown without changing the saved selection.
     *
     * @returns {Promise<void>}
     */
    async fetch_shared_link_dropdown_combis() {
        if (this.get_unique_gasspring_descriptions(5).length >= 2) {
            return;
        }

        var cookie_data = cf.cookie ? cf.cookie.get() : null;

        await this.calculate_length_combis_ajax();
        this.add_stored_combination_as_first(cookie_data);
        this.is_fresh_calculation = false;
        this.gs_desc_list = this.sorted_length_combis.map(function (item) {
            return item.gs;
        });
    }

    /**
     * Restore the saved combination from a shared link cookie without server recalculation.
     *
     * @param {Array|null} cookie_data Parsed configurator cookie data.
     * @returns {boolean} True when a combination was restored.
     */
    restore_from_cookie(cookie_data) {
        if (!cookie_data || !Array.isArray(cookie_data)) {
            return false;
        }

        var stored_gasspring = null;
        var stored_mp_cyl = null;
        var stored_mp_rod = null;
        var stored_extender_type = null;
        var length_combis_json = null;
        var gs_ranked_json = null;

        for (var i = 0; i < cookie_data.length; i++) {
            var item = cookie_data[i];
            if (item.name === 'gasspring') {
                stored_gasspring = item.value;
            } else if (item.name === 'mp_cyl') {
                stored_mp_cyl = item.value;
            } else if (item.name === 'mp_rod') {
                stored_mp_rod = item.value;
            } else if (item.name === 'extender_type') {
                stored_extender_type = item.value;
            } else if (item.name === 'length_combis') {
                length_combis_json = item.value;
            } else if (item.name === 'gs_ranked') {
                gs_ranked_json = item.value;
            }
        }

        if (!stored_gasspring || !stored_mp_cyl || !stored_mp_rod) {
            return false;
        }

        if (length_combis_json) {
            try {
                var parsed_combis = JSON.parse(length_combis_json);
                if (Array.isArray(parsed_combis) && parsed_combis.length > 0) {
                    this.sorted_length_combis = this.normalize_ranked_combos(parsed_combis);
                    this.gs_desc_list = this.sorted_length_combis.map(function (combo) {
                        return combo.gs;
                    });
                    this.add_stored_combination_as_first(cookie_data);
                    this.length_combi_index = this.find_stored_combination_index(cookie_data);
                    this.is_fresh_calculation = false;
                    return true;
                }
            } catch (e) {
                // Fall through to gs_ranked or single-combination restore.
            }
        }

        if (gs_ranked_json) {
            try {
                var parsed_ranked = JSON.parse(gs_ranked_json);
                if (Array.isArray(parsed_ranked) && parsed_ranked.length > 0) {
                    this.sorted_length_combis = this.normalize_ranked_combos(parsed_ranked);
                    this.gs_desc_list = this.sorted_length_combis.map(function (combo) {
                        return combo.gs;
                    });
                    this.add_stored_combination_as_first(cookie_data);
                    this.length_combi_index = this.find_stored_combination_index(cookie_data);
                    this.is_fresh_calculation = false;
                    return true;
                }
            } catch (e) {
                // Fall through to single-combination restore.
            }
        }

        if (!stored_extender_type && gs_ranked_json) {
            try {
                var ranked_for_extender = JSON.parse(gs_ranked_json);

                if (Array.isArray(ranked_for_extender)) {
                    for (var r = 0; r < ranked_for_extender.length; r++) {
                        var ranked_combo = ranked_for_extender[r];

                        if (ranked_combo.gs === stored_gasspring &&
                            ranked_combo.mp_cyl === stored_mp_cyl &&
                            ranked_combo.mp_rod === stored_mp_rod &&
                            ranked_combo.ex && ranked_combo.ex !== 'none') {
                            stored_extender_type = ranked_combo.ex;
                            break;
                        }
                    }
                }
            } catch (e) {
            }
        }

        var combination = {
            gs: stored_gasspring,
            mp_cyl: stored_mp_cyl,
            mp_rod: stored_mp_rod,
            ex: stored_extender_type || 'none',
            has_extender: !!(stored_extender_type && stored_extender_type !== 'none')
        };

        this.sorted_length_combis = [combination];
        this.gs_desc_list = [stored_gasspring];
        this.length_combi_index = 0;
        this.is_fresh_calculation = false;
        return true;
    }

    /**
     * Add the stored combination from cookie as the first (best) result
     * 
     * This ensures that when loading from a cookie, the stored combination
     * is always selected as the best option, even if the calculation
     * would normally rank it differently.
     */
    add_stored_combination_as_first(cookie_data) {
        // Check if we have cookie data and user is not actively making selections
        if (!cookie_data || !Array.isArray(cookie_data) || cf.user_making_selections) {
            return;
        }
        
        // Extract stored values from cookie
        var stored_gasspring = null;
        var stored_mp_cyl = null;
        var stored_mp_rod = null;
        var stored_extender_type = null;
        
        for (var i = 0; i < cookie_data.length; i++) {
            var item = cookie_data[i];
            if (item.name === 'gasspring') {
                stored_gasspring = item.value;
            } else if (item.name === 'mp_cyl') {
                stored_mp_cyl = item.value;
            } else if (item.name === 'mp_rod') {
                stored_mp_rod = item.value;
            } else if (item.name === 'extender_type') {
                stored_extender_type = item.value;
            }
        }
        
        // Check if we have the required stored values
        if (!stored_gasspring || !stored_mp_cyl || !stored_mp_rod) {
            return;
        }

        if (typeof cf !== 'undefined' && cf.resolve_extender_type_from_cookie) {
            var resolved_extender = cf.resolve_extender_type_from_cookie(cookie_data);

            if (resolved_extender) {
                stored_extender_type = resolved_extender;
            }
        }

        if (!stored_extender_type || stored_extender_type === 'none') {
            for (var j = 0; j < this.sorted_length_combis.length; j++) {
                var ranked_combo = this.sorted_length_combis[j];

                if (ranked_combo.gs === stored_gasspring &&
                    ranked_combo.mp_cyl === stored_mp_cyl &&
                    ranked_combo.mp_rod === stored_mp_rod &&
                    ranked_combo.ex && ranked_combo.ex !== 'none') {
                    stored_extender_type = ranked_combo.ex;
                    break;
                }
            }
        }
        
        // Look for the stored combination in the current results
        var stored_combination = null;
        for (var i = 0; i < this.sorted_length_combis.length; i++) {
            var combo = this.sorted_length_combis[i];
            if (combo.gs === stored_gasspring && 
                combo.mp_cyl === stored_mp_cyl && 
                combo.mp_rod === stored_mp_rod &&
                combo.ex === (stored_extender_type || 'none')) {
                stored_combination = combo;
                break;
            }
        }
        
        if (stored_combination) {
            this.sorted_length_combis = this.sorted_length_combis.filter(function(combo) {
                return !(combo.gs === stored_gasspring &&
                        combo.mp_cyl === stored_mp_cyl &&
                        combo.mp_rod === stored_mp_rod &&
                        combo.ex === (stored_extender_type || 'none'));
            });

            this.sorted_length_combis.unshift(stored_combination);
            return;
        }

        // Always reinject the cookie selection when it is missing from ranked results.
        // This covers alternate mounting parts (with or without extender), including when
        // use_extensions is on but no matching extender combo was ranked.
        this.sorted_length_combis.unshift({
            gs: stored_gasspring,
            mp_cyl: stored_mp_cyl,
            mp_rod: stored_mp_rod,
            ex: stored_extender_type || 'none',
            has_extender: !!(stored_extender_type && stored_extender_type !== 'none')
        });
    }

    /**
     * Set the selected combination and handle extension data
     * 
     * This method takes the combination at the current index and applies it to the configurator.
     * It handles both extension and non-extension combinations based on the server response.
     * 
     * EXTENSION LOGIC:
     * - If combination.ex !== 'none', find the extension data from ex_data array
     * - Set cf.ex globally so all components (3D rendering, pricing, cart) can access it
     * - If combination.ex === 'none', set cf.ex to null
     * - Update length warnings to reflect extension presence
     * 
     * @returns {Promise}
     */
    async set_length_combi() {
        if (this.sorted_length_combis.length && typeof (this.sorted_length_combis[this.length_combi_index]) !== 'undefined') {
            var combi = this.sorted_length_combis[this.length_combi_index]; // ← Get selected combination
            var current_gasspring = $('#gasspring').val();
    
            // CRITICAL FIX: 
            // - If this is a fresh calculation (is_fresh_calculation = true), set the gas spring from the combination
            // - If user manually selected a gas spring (is_fresh_calculation = false), respect their selection
            if (combi.gs === current_gasspring) {
                // Gas spring matches - still call change_gasspring to ensure force select is populated
                await cf.change_gasspring(false);
                this.is_fresh_calculation = false; // Reset flag after successful match
            } else if (this.is_fresh_calculation) {
                // This is a fresh calculation - set the gas spring from the best combination
                $('#gasspring').val(combi.gs);
                await cf.change_gasspring(false);
                this.is_fresh_calculation = false; // Reset flag after setting
            } else {
                // Gas spring doesn't match and this is NOT a fresh calculation - user manually selected something different
                // Don't override their selection, but still update mounting parts if possible
                // Find the correct combination index for the currently selected gas spring
                var correct_index = this.find_combination_index_by_gs(
                    current_gasspring,
                    cf.cookie ? cf.cookie.get() : null
                );
                if (correct_index !== -1 && correct_index < this.sorted_length_combis.length) {
                    this.length_combi_index = correct_index;
                    combi = this.sorted_length_combis[this.length_combi_index];
                } else {
                    return; // Can't proceed without matching combination
                }
            }
            
            // Update mounting parts based on the combination

            $('#mp_cyl').val(combi.mp_cyl); // ← Set cylinder mounting part
            $('#mp_rod').val(combi.mp_rod); // ← Set rod mounting part
            
            // Update the configurator's mounting part objects
            var mp_cyl_index = $('#mp_cyl :selected').data('i') ?? 0;
            var mp_rod_index = $('#mp_rod :selected').data('i') ?? 0;
            cf.mp_cyl = mp_data[mp_cyl_index];
            cf.mp_rod = mp_data[mp_rod_index];
            
            // ← EXTENSION HANDLING: Process extension data if present
            var use_extensions = $('#use_extension_checkbox').is(':checked');
            if (use_extensions && combi.ex && combi.ex !== 'none') {
                var extender_data = typeof cf.find_extension_data === 'function'
                    ? cf.find_extension_data(combi.ex)
                    : null;

                if (!extender_data && typeof ex_data !== 'undefined') {
                    for (var i = 0; i < ex_data.length; i++) {
                        if (ex_data[i].filename === combi.ex) {
                            extender_data = ex_data[i];
                            break;
                        }
                    }
                }

                if (extender_data) {
                    cf.ex = extender_data;
                    $('#use_extension_checkbox').prop('checked', true);
                    cf.set_length_warning();
                    cf.update_extension_control_visibility();
                }
            } else {
                // ← No extension selected - clear extension data
                cf.ex = null;
                cf.set_length_warning();
                cf.update_extension_control_visibility();
            }
            
            // ← Update the hidden extender_type field with the current combination's extender
            if (use_extensions && combi.ex && combi.ex !== 'none') {
                $('#extender_type').val(combi.ex);
            } else if (!$('#use_extension_checkbox').is(':checked')) {
                $('#extender_type').val('');
            } else {
                $('#extender_type').val('none');
            }
        } else {
            throw new Error('No length combi found by server');
        }
    }
}